import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { SheetContext } from '../../settings/sheet/context';
import { ListPageReset } from './ListPage';
import { ICON_BTN } from './ui';
import './listPage.css';

/**
 * Συρτάρι λεπτομερειών δεξιά (όπως το πάνελ του Καταλόγου και οι φόρμες Υπαλλήλου/Εκτυπωτή): τίτλος +
 * μία γραμμή, περιεχόμενο που κυλά (KeyValue, ή φόρμα με SheetField/SheetRow), κάτω τα κουμπιά —
 * `footer` δεξιά (Άκυρο GHOST_BTN, Αποθήκευση PRIMARY_BTN), `footerStart` αριστερά (Διαγραφή
 * DANGER_GHOST_BTN). Σε οθόνη κάτω από 640px πιάνει όλη την οθόνη.
 *
 * Παράθυρο (aria-modal) όπως ήταν τα παλιά παράθυρα επεξεργασίας: η λίστα από πίσω δεν πατιέται όσο
 * είναι ανοιχτό — μια φόρμα με αλλαγές δεν χάνεται από ένα κλικ σε άλλη γραμμή. Esc, το X και το κλικ
 * στο σκοτεινό φόντο καλούν onClose (η σελίδα αποφασίζει: π.χ. ρωτά αν υπάρχουν αλλαγές). Το focus μπαίνει
 * μέσα (στο ίδιο το συρτάρι, ή σε πεδίο με autoFocus), το Tab δεν βγαίνει έξω, και στο κλείσιμο
 * γυρίζει εκεί που ήταν (π.χ. στη γραμμή του πίνακα).
 *
 * Ζωγραφίζεται (portal) στη ρίζα του χώρου εργασίας (.role-workspace / .cashier-workspace — εκεί ζουν τα
 * tokens του θέματος), όχι μέσα στη σελίδα: δεν το κόβει ούτε το επηρεάζει κανένα φύλλο ή παράθυρο από
 * πάνω του, και ό,τι μπει μέσα (SheetSection, DataTable) δεν δηλώνεται στη σελίδα από κάτω.
 */

export interface DetailPanelProps {
  open: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  /** Δεξιά στο κάτω μέρος: Άκυρο + Αποθήκευση (ή «Κλείσιμο» + η κύρια ενέργεια). */
  footer?: React.ReactNode;
  /** Αριστερά στο κάτω μέρος: Διαγραφή / Απόσυρση. */
  footerStart?: React.ReactNode;
  /** md ≈ 560px (λεπτομέρειες, μικρή φόρμα), lg ≈ 760px (φόρμα με δύο στήλες). */
  width?: 'md' | 'lg';
  /** Όνομα του X (προεπιλογή «Κλείσιμο»). */
  closeLabel?: string;
  testId?: string;
  children: React.ReactNode;
}

const WORKSPACE = '.role-workspace, .cashier-workspace';
const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), '
  + 'textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const focusables = (root: HTMLElement): HTMLElement[] => Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
  .filter(el => !el.closest('[hidden], [inert], [aria-hidden="true"]'));

/** Focus στο πρώτο της λίστας που το δέχεται (ένα κρυφό με CSS απλώς δεν παίρνει focus — πάμε στο επόμενο). */
const focusFirst = (list: HTMLElement[]): void => {
  for (const el of list) {
    el.focus();
    if (document.activeElement === el) return;
  }
};

export const DetailPanel: React.FC<DetailPanelProps> = ({
  open, title, subtitle, onClose, footer, footerStart, width = 'md', closeLabel, testId = 'detail-panel', children,
}) => {
  const { t } = useTranslation(['listPage']);
  const uid = useId();
  const titleId = `${uid}-title`;
  const subtitleId = `${uid}-subtitle`;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Πού ήταν το focus ΠΡΙΝ ανοίξει: διαβάζεται στη ζωγραφιά που το ανοίγει, γιατί ένα πεδίο με autoFocus
  // μέσα στο συρτάρι το έχει ήδη πάρει όταν τρέχουν τα effects.
  const wasOpen = useRef(false);
  const restoreRef = useRef<HTMLElement | null>(null);
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpen.current = open;

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    setHost(anchor?.closest<HTMLElement>(WORKSPACE) ?? document.body);
  }, []);

  useEffect(() => {
    if (!open || !host) return undefined;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });

    // Esc από μέσα (ή χωρίς focus πουθενά). Όχι από άλλο παράθυρο έξω από το συρτάρι, και όχι αν ένα
    // κομμάτι μέσα το έχει ήδη χειριστεί (preventDefault — π.χ. το παράθυρο ημερομηνιών του DateRange).
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target as Node | null;
      const nowhere = !target || target === document.body || target === document.documentElement;
      if (!nowhere && !panelRef.current?.contains(target)) return;
      event.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Το restoreRef δείχνει πάντα κάτι ΕΞΩ από το συρτάρι (διαβάστηκε πριν ανοίξει).
      const back = restoreRef.current;
      if (back && back.isConnected) back.focus({ preventScroll: true });
    };
  }, [open, host]);

  // Το Tab κάνει κύκλο μέσα στο συρτάρι.
  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const panel = event.currentTarget;
    const items = focusables(panel);
    if (items.length === 0) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }
    const active = document.activeElement;
    const inside = active instanceof HTMLElement && panel.contains(active);
    if (event.shiftKey) {
      if (!inside || active === panel || active === items[0]) {
        event.preventDefault();
        focusFirst(items.slice().reverse());
      }
    } else if (!inside || active === items[items.length - 1]) {
      event.preventDefault();
      focusFirst(items);
    }
  };

  const panel = open && host ? createPortal(
    <SheetContext.Provider value={null}>
      <ListPageReset>
        <div className="lp-overlay" data-testid={`${testId}-overlay`}>
          <div className="lp-scrim" aria-hidden="true" onClick={() => onCloseRef.current()} data-testid={`${testId}-scrim`} />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={subtitle ? subtitleId : undefined}
            tabIndex={-1}
            className="lp-panel"
            data-width={width}
            data-testid={testId}
            onKeyDown={onPanelKeyDown}
          >
            <header className="lp-panel-head">
              <div className="lp-panel-head-text">
                <h2 id={titleId} className="lp-panel-title">{title}</h2>
                {subtitle && <p id={subtitleId} className="lp-panel-sub">{subtitle}</p>}
              </div>
              <button type="button" className={ICON_BTN} onClick={() => onCloseRef.current()}
                aria-label={closeLabel || t('listPage:close')} title={closeLabel || t('listPage:close')} data-testid={`${testId}-close`}>
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </header>
            <div className="lp-panel-body">{children}</div>
            {(footer || footerStart) && (
              <footer className="lp-panel-foot">
                {footerStart && <div className="lp-panel-foot-start">{footerStart}</div>}
                {footer && <div className="lp-panel-foot-end">{footer}</div>}
              </footer>
            )}
          </div>
        </div>
      </ListPageReset>
    </SheetContext.Provider>,
    host,
  ) : null;

  return (
    <>
      <span ref={anchorRef} hidden />
      {panel}
    </>
  );
};

export default DetailPanel;
